import { Grid, Paper, Typography } from "@mui/material"
import { Title } from "components/Common/Title"
import { TrainingReportParameterList } from "components/TrainingReportList/TrainingReportParameterList"
import { TrainingResultRadarChart } from "components/TrainingResult/TrainingResultRadarChart"
import { TrainingReportSuggestionList } from "components/TrainingResult/TrainingResultSuggestionList"
import { useAppDispatch, useAppSelector } from "hooks"
import { useEffect } from "react"
import { useParams } from "react-router-dom"
import lessonService from "services/lessonService"
import organizationService from "services/organizationService"
import trainingReportService from "services/trainingReportService"
import userService from "services/userService"
import { setCurrentLesson } from "slices/lessonSlice"
import { setCurrentOrganization } from "slices/organizationSlice"
import { setCurrentTrainingReport, setTrainingReports } from "slices/trainingReportSlice"
import { setSelectedUser } from "slices/usersSlice"
import { hiabColors } from "theme"
import type { ITrainingReport } from "types/trainingReportTypes"

export function Exam() {
  const dispatch = useAppDispatch()

  const currentOrganization = useAppSelector((state) => state.organization.currentOrganization)
  const selectedUser = useAppSelector((state) => state.users.selectedUser)
  const selectedTrainingReport = useAppSelector(
    (state) => state.trainingreport.currentTrainingReport,
  )

  const selectedLesson = useAppSelector((state) => state.lesson.currentLesson)

  const { id, userId, reportId } = useParams()

  useEffect(() => {
    if (currentOrganization === undefined && id) {
      organizationService
        .getOrganization(id)
        .then((organization) => {
          dispatch(setCurrentOrganization(organization))
        })
        .catch((err) => {
          console.log(err)
        })
    }

    if (userId && (selectedUser === undefined || (selectedUser && selectedUser.id !== userId))) {
      userService
        .getUser(userId)
        .then((user) => {
          dispatch(setSelectedUser(user))

          trainingReportService
            .getUserTrainingReports(user.id)
            .then((reports) => {
              dispatch(setTrainingReports(reports))
            })
            .catch((err) => {
              console.log(err)
            })
        })
        .catch((err) => {
          console.log(err)
        })
    }
    if (
      reportId &&
      (selectedTrainingReport === undefined ||
        (selectedTrainingReport && selectedTrainingReport.id !== reportId))
    ) {
      trainingReportService
        .getTrainingReport(reportId)
        .then((report) => {
          dispatch(setCurrentTrainingReport(report))

          lessonService
            .getLesson(report.lessonId)
            .then((lesson) => {
              dispatch(setCurrentLesson(lesson))
            })
            .catch((err) => {
              console.log(err)
            })
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }, [currentOrganization, dispatch, id, reportId, selectedTrainingReport, selectedUser, userId])

  const getCraneParameter = (trainingReport: ITrainingReport | undefined): string => {
    if (trainingReport === undefined || trainingReport.parameters === undefined) {
      return "-"
    }

    const craneParameter = trainingReport.parameters.find((x) => x.name === "crane")

    if (craneParameter) {
      return craneParameter.value
    }

    return "-"
  }

  const getToolParameter = (trainingReport: ITrainingReport | undefined): string => {
    if (trainingReport === undefined || trainingReport.parameters === undefined) {
      return "-"
    }

    const toolParameter = trainingReport.parameters.find((x) => x.name === "tool")

    if (toolParameter) {
      return toolParameter.value
    }

    return "-"
  }

  const getExamResultParameter = (trainingReport: ITrainingReport | undefined): string => {
    if (trainingReport === undefined || trainingReport.parameters === undefined) {
      return "-"
    }

    const toolParameter = trainingReport.parameters.find((x) => x.name === "exam_result")

    if (toolParameter) {
      return toolParameter.value
    }

    return "-"
  }

  const getExamFailureParameter = (
    trainingReport: ITrainingReport | undefined,
  ): string | undefined => {
    if (!trainingReport) {
      return undefined
    }

    const examFailureParameter = trainingReport.parameters.find(
      (x) => x.name === "exam_fail_reason",
    )

    if (!examFailureParameter) {
      return undefined
    }

    return examFailureParameter.value
  }

  const getExamDurationParameter = (trainingReport: ITrainingReport | undefined): string => {
    if (trainingReport === undefined || trainingReport.parameters === undefined) {
      return "-"
    }

    const toolParameter = trainingReport.parameters.find((x) => x.name === "exam_duration")

    if (toolParameter) {
      return `${Math.round(Number(toolParameter.value) / 60)}min`
    }

    return "-"
  }

  return (
    <Grid container alignItems={"flex-start"} spacing={3}>
      <Grid item container xs={6} spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 200 }}>
            {selectedUser && (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Title>EXAM DETAILS</Title>
                </Grid>
                <Grid item container>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">
                      User
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2" color="text.primary">
                      {`${selectedUser.firstname} ${selectedUser.lastname}`}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item container>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">
                      Exam
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2" color="text.primary">
                      {selectedLesson?.name}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item container>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">
                      Date
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2" color="text.primary">
                      {selectedTrainingReport &&
                        new Date(selectedTrainingReport.endDate).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item container>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">
                      Crane
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2" color="text.primary">
                      {getCraneParameter(selectedTrainingReport)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item container>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">
                      Tool
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body2" color="text.primary">
                      {getToolParameter(selectedTrainingReport)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            <Title>EXAM TASKS</Title>
            <Grid item xs>
              {selectedTrainingReport && (
                <TrainingReportParameterList
                  parameters={selectedTrainingReport?.parameters.filter((x) =>
                    x.name.includes("task"),
                  )}
                />
              )}
            </Grid>
          </Paper>
        </Grid>

        {selectedTrainingReport && selectedTrainingReport.parameters.length > 4 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
              <Title>USER PERFORMANCE CHART</Title>
              <TrainingResultRadarChart trainingReport={selectedTrainingReport} />
            </Paper>
          </Grid>
        )}
      </Grid>

      <Grid item container xs={6} spacing={3}>
        <Grid item xs={6}>
          <Paper
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: 200,
              justifyContent: "space-between",
            }}
          >
            {selectedUser && (
              <>
                <Grid item>
                  <Title>RESULT</Title>
                </Grid>
                <Grid container justifyContent="center" alignItems="flex-end" margin="auto">
                  <Typography
                    variant="h1"
                    color={
                      getExamResultParameter(selectedTrainingReport) === "Pass" ? "green" : "red"
                    }
                    sx={{
                      fontSize: 42,
                    }}
                  >
                    {getExamResultParameter(selectedTrainingReport)}
                  </Typography>
                </Grid>
                {getExamFailureParameter(selectedTrainingReport) && (
                  <Grid>
                    <Typography
                      component="h4"
                      variant="caption"
                      color={hiabColors.redPrimary}
                      gutterBottom
                    >
                      {getExamFailureParameter(selectedTrainingReport)}
                    </Typography>
                  </Grid>
                )}
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column", height: 200 }}>
            {selectedUser && (
              <Grid>
                <Grid item>
                  <Title>EXAM DURATION</Title>
                </Grid>
                <Grid container justifyContent="center" alignItems="flex-end" marginTop={5}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: 42,
                      fontWeight: 1000,
                    }}
                  >
                    {getExamDurationParameter(selectedTrainingReport)}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            <Title>SAFETY</Title>
            <Grid item xs>
              {selectedTrainingReport && (
                <TrainingReportParameterList
                  parameters={selectedTrainingReport?.parameters.filter((x) =>
                    x.name.includes("safety"),
                  )}
                />
              )}
            </Grid>
          </Paper>
        </Grid>

        {selectedTrainingReport && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
              <Title>SUGGESTIONS</Title>
              <TrainingReportSuggestionList trainingReport={selectedTrainingReport} />
            </Paper>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}
